import { Play, Pause, SkipBack, SkipForward } from "lucide-react";
import { useRef, useState, useCallback } from "react";

interface PlayerControlsProps {
  isPlaying: boolean;
  togglePlayPause: () => void;
  currentTime: number;
  duration: number;
  onSeek: (value: number[]) => void;
  formatTime: (timeInSeconds: number) => string;
}

export function PlayerControls({
  isPlaying,
  togglePlayPause,
  currentTime,
  duration,
  onSeek,
  formatTime,
}: PlayerControlsProps) {
  const progressRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!progressRef.current || duration <= 0) return;
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, clickX / rect.width));
      onSeek([percentage * duration]);
    },
    [duration, onSeek],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true);
      handleProgressClick(e);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!progressRef.current || duration <= 0) return;
        const rect = progressRef.current.getBoundingClientRect();
        const clickX = moveEvent.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, clickX / rect.width));
        onSeek([percentage * duration]);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [duration, onSeek, handleProgressClick],
  );

  return (
    <div className="flex flex-col items-center w-full gap-2.5 font-archivo">
      {/* Play controls */}
      <div className="flex items-center justify-center gap-6 w-full">
        <button
          onClick={() => console.log("Previous track - not implemented yet")}
          className="text-[#9A96C4] hover:text-white transition-colors duration-200 flex-shrink-0"
          title="Previous track"
        >
          <SkipBack className="h-5 w-5" />
        </button>

        <button
          onClick={togglePlayPause}
          className="h-[52px] w-[52px] rounded-full bg-white/10 hover:bg-white/[0.16] flex items-center justify-center transition-all duration-200 flex-shrink-0"
          title={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <Pause className="h-6 w-6 text-white" />
          ) : (
            <Play className="h-6 w-6 text-white ml-0.5" />
          )}
        </button>

        <button
          onClick={() => console.log("Next track - not implemented yet")}
          className="text-[#9A96C4] hover:text-white transition-colors duration-200 flex-shrink-0"
          title="Next track"
        >
          <SkipForward className="h-5 w-5" />
        </button>
      </div>

      {/* Progress bar - matching collapsed player style */}
      <div className="w-full flex items-center gap-2 px-2">
        <span className="text-[11px] font-jetbrains text-[#9A96C4] min-w-[35px] text-left tabular-nums">
          {formatTime(currentTime)}
        </span>
        <div
          ref={progressRef}
          className="flex-1 h-1 bg-white/[0.16] rounded-full cursor-pointer group relative min-w-0"
          onClick={handleProgressClick}
          onMouseDown={handleMouseDown}
        >
          <div
            className="h-full bg-[#8B6BF0] rounded-full transition-all duration-150 ease-linear"
            style={{ width: `${progress}%` }}
          />
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-[#8B6BF0] rounded-full transition-opacity ${isDragging ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>
        <span className="text-[11px] font-jetbrains text-[#9A96C4] min-w-[35px] text-right tabular-nums">
          {formatTime(duration)}
        </span>
      </div>
    </div>
  );
}
